import { useState } from 'react';
import useNotification from '@/hooks/useNotification';
import { NotificationSeverity } from '@/components/notifications/Notification';
import { mapAddShippingsError } from '@/utils/errorUtils';
import { getScopeErrorMessage } from '../utils/getScopeErrorMessage';
import { useAddShippingsToRouteMutation } from '../services';
import {
  AddShippingsToRouteSchema,
  type AddShippingsToRouteInput,
} from '../schemas/addShippingsToRoute.schema';

export interface UseAssignShippingsToRouteParams {
  routeId: string;
  logisticOperatorId: string;
  onSuccess?: () => void;
}

const VALIDATION_ERROR_MESSAGE =
  'Datos inválidos. Verifique que seleccionó al menos un pedido.';

export function useAssignShippingsToRoute({
  routeId,
  logisticOperatorId,
  onSuccess,
}: UseAssignShippingsToRouteParams) {
  const { showNotification } = useNotification();
  const [error, setError] = useState<string | null>(null);

  const [addShippings, { isLoading }] = useAddShippingsToRouteMutation();

  const assign = async (input: AddShippingsToRouteInput): Promise<boolean> => {
    const parsed = AddShippingsToRouteSchema.safeParse(input);
    if (!parsed.success) {
      const firstError = parsed.error.errors[0];
      const message =
        firstError?.message ?? VALIDATION_ERROR_MESSAGE;
      setError(message);
      showNotification(message, NotificationSeverity.error);
      return false;
    }

    setError(null);
    try {
      await addShippings({
        routeId,
        logisticOperatorId,
        shippingNumbers: parsed.data.shippingNumbers,
        type: parsed.data.type,
        lat: parsed.data.lat,
        long: parsed.data.long,
      }).unwrap();

      showNotification(
        'Pedidos agregados y ruta recalculada exitosamente',
        NotificationSeverity.success
      );
      onSuccess?.();
      return true;
    } catch (err) {
      const message =
        getScopeErrorMessage(err) ??
        mapAddShippingsError(err, 'Error al asignar pedidos a la ruta');
      setError(message);
      showNotification(message, NotificationSeverity.error);
      return false;
    }
  };

  return { assign, isLoading, error };
}
